import { AxiosError } from 'axios'
import { StatusCodes } from 'http-status-codes'
import { useParams, useSearchParams } from 'react-router'

import { WILL_FAMILY_PROTECTION_OPTION } from '@/components/flows/CompassFlow/constants'
import {
  BusinessAssetsInput,
  LiabilitiesInput,
  ShareInput,
} from '@/components/flows/CompassFlow/types'
import { TooltipHint } from '@/components/form/FieldLabel'
import { DATE_FORMATS } from '@/lib/constants'
import { formatDate } from '@/lib/helpers/dateHelpers'
import { isConstraintViolationListError } from '@/lib/helpers/helperFunctions'
import { isRoute } from '@/lib/helpers/routeHelpers'
import { ROUTES } from '@/lib/routes'
import { SEARCH_PARAMS } from '@/lib/searchParams'
import { SelectOption } from '@/types/misc'
import { CompassMetadataItem } from '@/types/responses/compass'

export function willSellBusiness(businessAssets?: BusinessAssetsInput) {
  const shares =
    businessAssets?.shares && Array.isArray(businessAssets.shares)
      ? businessAssets.shares
      : []

  if (shares.length === 0) {
    return false
  }

  const wantsToSellShares = shares.some((share) => share.want_to_sell_shares)
  const hasProvidedSellYear = shares.some((share) => share.year)
  const hasProvidedExpectedSaleValue = shares.some(
    (share) => typeof share.expected_value === 'number',
  )
  const hasProvidedSaleValueConfidence = shares.some(
    (share) => typeof share.confidence === 'number',
  )

  return !!(
    businessAssets &&
    wantsToSellShares &&
    hasProvidedSellYear &&
    hasProvidedExpectedSaleValue &&
    hasProvidedSaleValueConfidence
  )
}

export function getCompleteShareInputs(
  businessAssets?: BusinessAssetsInput,
): ShareInput[] {
  const shares = businessAssets?.shares || []
  return shares.filter(isCompleteShareInput)
}

export function isCompleteShareInput(share: ShareInput) {
  const wantsToSellShares = share.want_to_sell_shares === true
  const hasProvidedSellYear = share.year
  const hasProvidedExpectedSaleValue = typeof share.expected_value === 'number'
  const hasProvidedSaleValueConfidence = typeof share.confidence === 'number'

  return !!(
    wantsToSellShares &&
    hasProvidedSellYear &&
    hasProvidedExpectedSaleValue &&
    hasProvidedSaleValueConfidence
  )
}

export function hasFinancialDependant(
  liabilitiesInput: LiabilitiesInput,
): boolean {
  return (
    liabilitiesInput.has_financial_dependants === true &&
    Array.isArray(liabilitiesInput.financial_dependants) &&
    liabilitiesInput.financial_dependants.some(
      (financialDependant) =>
        !!financialDependant.financial_dependant_birth_month &&
        !!financialDependant.financial_dependant_birth_year,
    )
  )
}

export function normalizeClientDateOfBirth(
  dateOfBirth?: string | null,
): string {
  if (!dateOfBirth) {
    return ''
  }

  return formatDate(dateOfBirth, DATE_FORMATS.DAY_MONTH_YEAR)
}

export function useCompassParams() {
  const [searchParams] = useSearchParams()
  const params = useParams()

  const clientId = searchParams.get(SEARCH_PARAMS.userCompass.clientId)
    ? Number(searchParams.get(SEARCH_PARAMS.userCompass.clientId))
    : null

  const referralId = searchParams.get(SEARCH_PARAMS.userCompass.referralId)
    ? Number(searchParams.get(SEARCH_PARAMS.userCompass.referralId))
    : null

  const compassReportId = searchParams.get(
    SEARCH_PARAMS.userCompass.compassReportId,
  )
    ? Number(searchParams.get(SEARCH_PARAMS.userCompass.compassReportId))
    : null

  const willShareWithClient =
    searchParams.get(SEARCH_PARAMS.userCompass.willShareWithClient) === 'true'

  const token =
    params.token ||
    searchParams.get(SEARCH_PARAMS.userCompass.token) ||
    undefined

  const companyReferralCode =
    params[SEARCH_PARAMS.userCompass.companyReferralCode]

  const step = searchParams.get(SEARCH_PARAMS.userCompass.step)

  const inviteClientsTab = searchParams.get(
    SEARCH_PARAMS.userCompass.inviteClientsTab,
  )

  const returnUrl = searchParams.get(SEARCH_PARAMS.userCompass.returnUrl)

  const withCompany = searchParams.get(SEARCH_PARAMS.userCompass.withCompany)
    ? Number(searchParams.get(SEARCH_PARAMS.userCompass.withCompany))
    : undefined

  return {
    clientId,
    referralId,
    compassReportId,
    token,
    companyReferralCode,
    returnUrl: returnUrl,
    step,
    inviteClientsTab,
    willShareWithClient,
    withCompany,
  }
}

// The Python API accepts options in fully-formed label formats
// (e.g., "Income Tax" instead of something like "income_tax").
// This function creates an option object the uses the same value for
// both `label` and `value`.

export function makeOption(
  label: string,
  tooltipHint?: TooltipHint,
): SelectOption {
  return { label, value: label, tooltipHint }
}

export type ExistingCompanyClientError = AxiosError<{
  code: 'existing-company-client'
  message: string
}>

export function isExistingCompanyClientError(
  error: unknown,
): error is ExistingCompanyClientError {
  return (
    error instanceof AxiosError &&
    error.response?.status === StatusCodes.BAD_REQUEST &&
    error.response?.data?.code === 'existing-company-client'
  )
}

export function isUnauthenticatedClientFlow(metadata?: CompassMetadataItem) {
  return !!metadata?.isUnauthenticatedClient || isRoute(ROUTES._userCompass)
}

export function hasWillFamilyProtection(
  familyProtectionOptions: LiabilitiesInput['family_protection_options'],
) {
  return (
    familyProtectionOptions &&
    familyProtectionOptions.includes(WILL_FAMILY_PROTECTION_OPTION)
  )
}

export function shouldRetryCompassRequest(
  failureCount: number,
  error: unknown,
): boolean {
  // Don't retry if it's a validation error
  if (isConstraintViolationListError(error)) {
    return false
  }

  // Otherwise, try 3 times
  return failureCount < 3
}
